import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";

import { logEvent } from "@/lib/client-log";
import { getSessionToken } from "@/lib/session";

// Use environment variable or fallback to production
const backendBaseURL = import.meta.env.VITE_API_URL || "https://server.saasaki.com/api";

export const axiosInstance = axios.create({
  baseURL: backendBaseURL,
  withCredentials: true,
});

type TimedConfig = InternalAxiosRequestConfig & { _startedAt?: number };

/**
 * Count rows in a response without shipping its contents — the length of every
 * top-level array. This is what turns "the page looked empty" into a fact:
 * `resultCounts.employees = 0` on a healthy 200.
 */
function resultCounts(data: unknown): Record<string, number> | undefined {
  if (!data || typeof data !== "object" || Array.isArray(data)) return undefined;
  const counts: Record<string, number> = {};
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    if (Array.isArray(value)) counts[key] = value.length;
  }
  return Object.keys(counts).length ? counts : undefined;
}

const path = (config?: { url?: string; baseURL?: string }) =>
  (config?.url ?? "").split("?")[0];

// What a caller sends when it read the cookie while the cookie was missing.
const NO_TOKEN = /^Bearer\s*(undefined|null)?$/;

// Request bodies are intentionally never logged from the browser: the login
// payload holds a password, and the safest redaction is not sending it at all.
axiosInstance.interceptors.request.use((config) => {
  (config as TimedConfig)._startedAt = Date.now();

  // Such a request can only ever be a 401. If this page still holds the
  // session's token, send that instead (see lib/session).
  const sent = config.headers.get("Authorization");
  if (typeof sent === "string" && NO_TOKEN.test(sent.trim())) {
    const token = getSessionToken();
    if (token) config.headers.set("Authorization", `Bearer ${token}`);
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    const started = (response.config as TimedConfig)._startedAt;
    logEvent({
      kind: "api_call",
      method: response.config.method?.toUpperCase(),
      path: path(response.config),
      status: response.status,
      durationMs: started ? Date.now() - started : undefined,
      resultCounts: resultCounts(response.data),
    });
    return response;
  },
  (error: AxiosError) => {
    const started = (error.config as TimedConfig | undefined)?._startedAt;
    logEvent({
      kind: "api_error",
      method: error.config?.method?.toUpperCase(),
      path: path(error.config),
      status: error.response?.status,
      durationMs: started ? Date.now() - started : undefined,
      code: error.code,
      message: error.message,
      serverError:
        typeof (error.response?.data as { error?: unknown } | undefined)?.error === "string"
          ? ((error.response?.data as { error: string }).error).slice(0, 500)
          : undefined,
    });
    return Promise.reject(error);
  }
);
